/**
 * Dinheiro trafega na API como número em reais com 2 casas (ex.: 420.5),
 * mas todo cálculo é feito em centavos inteiros para não acumular erro de ponto flutuante.
 */
export const toCents = (value: number | string | null | undefined): number => {
  const n = typeof value === 'string' ? Number(value) : value ?? 0;
  if (!Number.isFinite(n)) return 0;
  return Math.round((n as number) * 100);
};

export const fromCents = (cents: number): number => Math.round(cents) / 100;

export const ITEM_TYPES = ['PRODUCT', 'SERVICE'] as const;
export type ItemType = (typeof ITEM_TYPES)[number];

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  PRODUCT: 'Peça / produto',
  SERVICE: 'Serviço',
};

export interface PricedItem {
  type: ItemType;
  quantity: number;
  unitPrice: number;
  approved?: boolean;
}

export function itemTotalCents(item: Pick<PricedItem, 'quantity' | 'unitPrice'>): number {
  const qty = Number(item.quantity) || 0;
  return Math.round(qty * toCents(item.unitPrice));
}

export interface OrderTotals {
  partsTotal: number;
  servicesTotal: number;
  /** Desconto aplicado (R$). */
  discount: number;
  /** Total já com o desconto. */
  total: number;
}

export const DISCOUNT_TYPES = ['VALUE', 'PERCENT'] as const;
export type DiscountType = (typeof DISCOUNT_TYPES)[number];

export interface DiscountSpec {
  type: DiscountType | null | undefined;
  value: number;
}

/** Desconto em centavos sobre o subtotal; nunca passa do subtotal. */
export function discountCents(subtotalCents: number, spec?: DiscountSpec | null): number {
  if (!spec || !spec.type || !(spec.value > 0)) return 0;
  const raw = spec.type === 'PERCENT' ? Math.round((subtotalCents * Math.min(spec.value, 100)) / 100) : toCents(spec.value);
  return Math.max(0, Math.min(raw, subtotalCents));
}

/** Taxa da maquininha em centavos: percentual do valor recebido ou valor fixo. */
export function feeCents(amountCents: number, type: DiscountType | null | undefined, value: number): number {
  if (!type || !(value > 0)) return 0;
  const raw = type === 'PERCENT' ? Math.round((amountCents * Math.min(value, 100)) / 100) : toCents(value);
  return Math.max(0, Math.min(raw, amountCents));
}

/** Subtotal de peças, subtotal de serviços e total — mesma regra do protótipo (`calcDetail`). */
export function computeOrderTotals(items: readonly PricedItem[], discount?: DiscountSpec | null): OrderTotals {
  let parts = 0;
  let services = 0;
  for (const item of items) {
    // Itens desmarcados/reprovados não somam no total da OS
    if (item.approved === false) continue;
    const value = itemTotalCents(item);
    if (item.type === 'PRODUCT') parts += value;
    else services += value;
  }
  const disc = discountCents(parts + services, discount);
  return {
    partsTotal: fromCents(parts),
    servicesTotal: fromCents(services),
    discount: fromCents(disc),
    total: fromCents(parts + services - disc),
  };
}

export function formatBRL(value: number | string | null | undefined, opts: { cents?: boolean } = {}): string {
  const n = typeof value === 'string' ? Number(value) : value ?? 0;
  return (Number.isFinite(n) ? (n as number) : 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: opts.cents === false ? 0 : 2,
    maximumFractionDigits: opts.cents === false ? 0 : 2,
  });
}
